import { supabase } from '@/integrations/supabase/client';
import { Company, CompanyOverview } from '@/types/company';
import { handleError } from '@/utils/handleError';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

export class CompanyApiClient {
  /**
   * Get company for the current user
   * @returns Company data
   */
  static async getCompany(): Promise<Company> {
    try {
      const { data, error } = await supabase.functions.invoke('company-api', {
        body: { action: 'getCompany' },
      });

      if (error) throw error;

      return data.company;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchCompanyData'), 'getCompany');
      throw error;
    }
  }

  /**
   * Get all companies for the current user
   * @returns Array of company overviews
   */
  static async getAllCompanies(): Promise<CompanyOverview[]> {
    try {
      const { data, error } = await supabase.functions.invoke('company-api', {
        body: { action: 'getAllCompaniesForUser' },
      });

      if (error) throw error;

      return data.companies;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchCompanies'), 'getAllCompanies');
      throw error;
    }
  }

  /**
   * Switch to a different company
   * @param companyId The ID of the company to switch to
   * @returns True if successful
   */
  static async switchCompany(companyId: string | null): Promise<boolean> {
    try {
      const { data, error } = await supabase.functions.invoke('company-api', {
        body: { action: 'switchCompany', companyId },
      });

      if (error) throw error;

      return data.success;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToSwitchCompany'), 'switchCompany');
      throw error;
    }
  }
} 